import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { Box, keyframes, Stack } from "@mui/material";

import { RuleGraphView } from "./RuleGraphView";
import { RULE_GRAPH_SKELETON_PLACEHOLDER } from "./skeleton";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const REDUCED_MOTION = "@media (prefers-reduced-motion: reduce)";

const VISUALLY_HIDDEN = {
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: "1px",
  overflow: "hidden",
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
} as const;

/**
 * A graph that does not exist yet, shown as the thing being built: the
 * placeholder graph in skeleton mode. The placeholder's shape is generic
 * scenery, not the rule's real structure — the copy is what keeps that honest.
 *
 * Shared by the Selection logic tab's pending state and the create flow's
 * designing view, so the canvas the author watches is the same one the
 * finished graph lands on — and inset as the loaded canvas is, 16px on every
 * side (`SelectionLogicTab`), so the page does not shift when it lands.
 *
 * The designing view shows no heading (ENG-7894): the chat beside it is
 * already the story, so its copy is announced to assistive tech only.
 */
export const GraphBuildingState = ({
  title,
  hint,
  showHeading = true,
}: {
  title: string;
  hint: string;
  showHeading?: boolean;
}) => (
  <Stack sx={{ flex: 1, minHeight: 0, p: 4 }} spacing={1}>
    <Stack
      role="status"
      spacing={0.25}
      sx={showHeading ? undefined : VISUALLY_HIDDEN}
    >
      <IgTypography variant="body1" fontWeight={600}>
        {title}
      </IgTypography>
      <IgTypography variant="body2" color="text.secondary">
        {hint}
      </IgTypography>
    </Stack>
    <Box
      sx={{
        animation: `${fadeIn} 240ms ease-out`,
        display: "flex",
        flex: 1,
        minHeight: 0,
        [REDUCED_MOTION]: { animation: "none" },
      }}
    >
      <RuleGraphView graph={RULE_GRAPH_SKELETON_PLACEHOLDER} skeleton />
    </Box>
  </Stack>
);
